import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

import { AdminTrash } from 'components/admin/lib/admin_layout';
import Button, { ButtonTypes } from 'components/common/button';
import ConfirmOrCancelModal from 'components/common/confirm_or_cancel_modal';
import { Table, TBody, TD, TH, THead, TR } from 'components/common/table';

const WebhookTable = styled(Table)`
  margin-top: ${p => p.theme.spacing.stackMedium};
`;

const WebhookTR = styled(TR)`
  &:hover {
    color: ${p => p.theme.colors.green400};
    cursor: pointer;
  }
  &:hover .adminTrash {
    visibility: visible;
  }
`;

export const WebhookTrash = styled(AdminTrash)`
  visibility: hidden;
  margin-bottom: 5px;
  margin-right: ${p => p.theme.spacing.inlineLarge};
`;

const WebhookControlsTD = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-right: ${p => p.theme.spacing.insetSmall};
`;

const WebhookTD = styled(TD)`
  word-break: break-word;
`;

const StatusButtonOff = styled(Button)`
  background: ${p => p.theme.colors.gray400};
  color: ${p => p.theme.colors.white};
  width: 55px;
`;

const StatusButton = styled(Button)`
  width: 55px;
`;

const EmptyName = styled.div`
  color: ${p => p.theme.colors.gray600};
`;

export default function WebhooksTable({ webhooks, onClickWebhook, updateWebhook, deleteWebhook }) {
  return (
    <WebhookTable zebra inset="small">
      <THead>
        <TR>
          <TH>Name</TH>
          <TH>URL</TH>
          <TH>Events</TH>
          <TH>Status</TH>
        </TR>
      </THead>
      <TBody>
        {_.map(_.orderBy(webhooks, ['name', 'url'], ['asc', 'asc']), webhook => (
          <WebhookRow
            key={webhook.id}
            webhook={webhook}
            onClickWebhook={onClickWebhook}
            updateWebhook={updateWebhook}
            deleteWebhook={deleteWebhook}
          />
        ))}
      </TBody>
    </WebhookTable>
  );
}

WebhooksTable.propTypes = {
  deleteWebhook: PropTypes.func.isRequired,
  onClickWebhook: PropTypes.func.isRequired,
  updateWebhook: PropTypes.func.isRequired,
  webhooks: PropTypes.arrayOf(PropTypes.object),
};

export function WebhookRow({ webhook, onClickWebhook, updateWebhook, deleteWebhook }) {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [pendingWebhookId, setPendingWebhookId] = useState(null);

  function toggleWebhookStatus() {
    webhook.toggleStatus();
    updateWebhook(webhook);
  }

  function renderStatusButton() {
    if (webhook.enabled) {
      return (
        <StatusButton onClick={() => toggleWebhookStatus()} buttonType={ButtonTypes.PRIMARY}>
          On
        </StatusButton>
      );
    }
    return (
      <StatusButtonOff onClick={() => toggleWebhookStatus()} buttonType={ButtonTypes.PRIMARY}>
        Off
      </StatusButtonOff>
    );
  }

  function renderDeleteConfirmationModal() {
    if (!isDeleteModalVisible) return null;

    return (
      <ConfirmOrCancelModal
        title="Permanently remove this webhook?"
        submitText="Remove"
        onCancel={onCancelDeleteWebhook}
        onSubmit={onConfirmDeleteWebhook}
      />
    );
  }

  function onClickRemoveWebhook(webhookId) {
    setIsDeleteModalVisible(true);
    setPendingWebhookId(webhookId);
  }

  function onCancelDeleteWebhook() {
    setIsDeleteModalVisible(false);
    setPendingWebhookId(null);
  }

  function onConfirmDeleteWebhook() {
    pendingWebhookId && deleteWebhook(pendingWebhookId);
    onCancelDeleteWebhook();
  }

  return (
    <WebhookTR data-aid="webhook-row">
      <WebhookTD data-aid="webhook-row-name" onClick={() => onClickWebhook(webhook.id)}>
        {webhook.name || <EmptyName>Name your webhook</EmptyName>}
      </WebhookTD>
      <WebhookTD onClick={() => onClickWebhook(webhook.id)}>{webhook.url}</WebhookTD>
      <TD onClick={() => onClickWebhook(webhook.id)}>{webhook.events[0]}</TD>
      <TD>
        <WebhookControlsTD>
          {renderStatusButton()}
          <WebhookTrash data-aid="webhook-row-delete" onClick={() => onClickRemoveWebhook(webhook.id)} />
          {renderDeleteConfirmationModal()}
        </WebhookControlsTD>
      </TD>
    </WebhookTR>
  );
}

WebhookRow.propTypes = {
  webhook: PropTypes.object,
  onClickWebhook: PropTypes.func.isRequired,
  deleteWebhook: PropTypes.func.isRequired,
  updateWebhook: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/integrations/webhooks_table.jsx